import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLogin, useWebAuthn, isWebAuthnSupported } from '@theraply/api-client';
import { Button, Input } from '@theraply/ui';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';

type AuthMethod = 'password' | 'passkey';

export default function LoginPage() {
  const navigate = useNavigate();
  const [authMethod, setAuthMethod] = useState<AuthMethod>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { mutate: login, isPending } = useLogin();
  const { login: webAuthnLogin, isSupported: webAuthnSupported } = useWebAuthn();

  const handlePasswordLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    login(
      { email, password, userType: 'admin' },
      {
        onSuccess: (data) => {
          if (data.user.role === 'admin') {
            navigate('/');
          } else {
            setError('Access denied. Admin privileges required.');
          }
        },
        onError: (err) => {
          setError(err.message || 'Invalid email or password');
        },
      }
    );
  };

  const handlePasskeyLogin = () => {
    setError(null);
    webAuthnLogin.mutate(
      { email, userType: 'admin' },
      {
        onSuccess: (data) => {
          if (data.user.role === 'admin') {
            navigate('/');
          } else {
            setError('Access denied. Admin privileges required.');
          }
        },
        onError: (err) => {
          setError(err.message || 'Passkey authentication failed');
        },
      }
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <ShieldCheckIcon className="h-16 w-16 text-red-500" />
          </div>
          <h1 className="mt-4 text-center text-4xl font-bold text-white">Theraply</h1>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            Admin Dashboard
          </h2>
          <p className="mt-2 text-center text-sm text-gray-400">
            Sign in with your admin credentials
          </p>
        </div>

        {/* Auth method tabs - only show if WebAuthn is supported */}
        {webAuthnSupported && (
          <div className="flex border-b border-gray-700">
            <button
              onClick={() => {
                setAuthMethod('password');
                setError(null);
              }}
              className={`flex-1 py-3 px-4 text-sm font-medium text-center border-b-2 transition-colors ${
                authMethod === 'password'
                  ? 'border-red-500 text-red-500'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              Password
            </button>
            <button
              onClick={() => {
                setAuthMethod('passkey');
                setError(null);
              }}
              className={`flex-1 py-3 px-4 text-sm font-medium text-center border-b-2 transition-colors ${
                authMethod === 'passkey'
                  ? 'border-red-500 text-red-500'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              Passkey
            </button>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="rounded-md bg-red-900/50 p-4 border border-red-700">
            <p className="text-sm text-red-200">{error}</p>
          </div>
        )}

        {/* Password authentication */}
        {authMethod === 'password' && (
          <form className="mt-8 space-y-6" onSubmit={handlePasswordLogin}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                  Email address
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                  fullWidth
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                  Password
                </label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                  fullWidth
                />
              </div>
            </div>

            <Button type="submit" variant="danger" fullWidth isLoading={isPending}>
              Sign in
            </Button>
          </form>
        )}

        {/* Passkey authentication */}
        {authMethod === 'passkey' && (
          <div className="mt-8 space-y-6">
            <div className="space-y-4">
              <p className="text-sm text-gray-400">
                Use your security key or device biometrics to authenticate.
              </p>
              <div>
                <label htmlFor="passkey-email" className="block text-sm font-medium text-gray-300">
                  Email address (optional)
                </label>
                <Input
                  id="passkey-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                  fullWidth
                />
              </div>
            </div>

            <Button
              onClick={handlePasskeyLogin}
              variant="danger"
              fullWidth
              isLoading={webAuthnLogin.isPending}
            >
              Sign in with Passkey
            </Button>
          </div>
        )}

        <div className="mt-6">
          <p className="text-center text-xs text-gray-500">
            This is a restricted area. All access attempts are logged and monitored.
          </p>
        </div>
      </div>
    </div>
  );
}
